import React, { useState, useEffect } from 'react';
import { FaMugHot, FaServer, FaShieldAlt, FaMagic, FaHeart } from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Button } from '../components/ds/core/Button';
import AdSlot from '../components/AdSlot';
import './SupportView.css';

const MONTHLY_GOAL = 100; // USD

function SupportView() {
  const tiles = [
    { icon: <FaServer />, label: 'Server costs' },
    { icon: <FaShieldAlt />, label: 'Moderation' },
    { icon: <FaMagic />, label: 'New features' },
  ];

  const [raised, setRaised] = useState(0);
  const [loadingDonations, setLoadingDonations] = useState(true);

  useEffect(() => {
    const loadDonations = async () => {
      try {
        const now = new Date();
        const ym = now.getUTCFullYear() + '-' + String(now.getUTCMonth() + 1).padStart(2, '0');
        const snap = await getDoc(doc(db, 'stats', 'donations'));
        const total = snap.exists() && snap.data()[ym] ? Number(snap.data()[ym]) : 0;
        setRaised(isFinite(total) ? total : 0);
      } catch (e) {
        console.error('Failed to load donations:', e);
      } finally {
        setLoadingDonations(false);
      }
    };
    loadDonations();
  }, []);

  const pct = Math.min(100, Math.round((raised / MONTHLY_GOAL) * 100));
  const monthName = new Date().toLocaleString('default', { month: 'long' });

  return (
    <div className="support-page">
      <div className="support-inner">
        <div className="support-card">
          <div className="support-emoji">☕</div>
          <h1>Support Yapp</h1>
          <p>
            Yapp is built and run by one person and kept free for everyone. If it's
            brightened your day, a small tip helps cover servers and keeps the globe
            spinning. Every coffee means the world. 💜
          </p>
          <a href="https://ko-fi.com/emileb" target="_blank" rel="noopener noreferrer" className="support-link">
            <Button variant="primary" size="lg" icon={<FaMugHot />}>Buy me a coffee on Ko-fi</Button>
          </a>
          <p className="support-note">Opens ko-fi.com/emileb in a new tab</p>
        </div>

        <div className="support-tiles">
          {tiles.map((t) => (
            <div key={t.label} className="support-tile">
              <span className="support-tile-icon">{t.icon}</span>
              <div className="support-tile-label">{t.label}</div>
            </div>
          ))}
        </div>

        <div
          className="support-donation-bar"
          style={{
            width: '100%',
            maxWidth: '520px',
            margin: '2rem auto 0',
            padding: '1.15rem 1.35rem',
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--border-subtle, rgba(0,0,0,0.1))',
            borderRadius: 'var(--radius-lg, 16px)',
            boxShadow: 'var(--shadow-md)',
            color: 'var(--text-body)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontWeight: 700, color: 'var(--text-strong)' }}>
              <FaHeart style={{ color: 'var(--brand-500)' }} /> Donations this month
            </span>
            <span style={{ fontWeight: 800, color: 'var(--brand-600, var(--brand-500))' }}>
              ${raised.toFixed(2)} <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>/ ${MONTHLY_GOAL}</span>
            </span>
          </div>
          <div
            style={{
              height: '14px',
              borderRadius: '999px',
              background: 'var(--brand-50, rgba(0,0,0,0.06))',
              border: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${pct}%`,
                borderRadius: '999px',
                background: 'var(--brand-gradient, linear-gradient(90deg, #667eea, #764ba2))',
                transition: 'width 0.6s ease',
              }}
            />
          </div>
          <p style={{ marginTop: '0.55rem', fontSize: '0.85rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            {loadingDonations
              ? 'Loading…'
              : raised >= MONTHLY_GOAL
                ? `🎉 ${monthName}'s goal reached — thank you so much!`
                : raised > 0
                  ? `${pct}% of ${monthName}'s goal — thank you to everyone who chipped in!`
                  : `Be the first to support Yapp this ${monthName}. 💜`}
          </p>
        </div>

        <AdSlot />
      </div>
    </div>
  );
}

export default SupportView;
